import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule } from '@ngx-translate/core';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';

export type SourcesDialogKSKind = 'url' | 'pdf' | 'file' | 'custom';

export interface SourcesDialogKnowledgeSource {
  id: string;
  kind: SourcesDialogKSKind;
  name: string;
  subtitle?: string;
}

export interface KnowledgeBaseSourcesDialogData {
  kbId: string;
  kbName: string;
  kbDescription?: string;
  /** When set, the KB is read-only (shared from elsewhere). */
  managedBy?: string;
  currentSources: SourcesDialogKnowledgeSource[];
  /** All owned sources the user could add; ignored for a read-only KB. */
  availableSources: SourcesDialogKnowledgeSource[];
  /** Persists the edited membership; omitted for a read-only KB (which never applies). */
  onApply?: KnowledgeBaseSourcesApplyFn;
}

export interface KnowledgeBaseSourcesResult {
  kbId: string;
  sourceIds: string[];
}

/**
 * Persists the edited source membership. Resolves on success (the dialog then closes); rejects to keep the dialog open with the user's selection intact while an error snackbar is shown.
 */
export type KnowledgeBaseSourcesApplyFn = (result: KnowledgeBaseSourcesResult) => Promise<void>;

/** Dialog for viewing/editing the sources inside a KB; read-only for "managed by" KBs. */
@Component({
  selector: 'ai-knowledge-base-sources-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    GalaxyFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslateModule,
    AiKnowledgeI18nModule,
  ],
  templateUrl: './knowledge-base-sources-dialog.component.html',
  styleUrls: ['./knowledge-base-sources-dialog.component.scss'],
})
export class KnowledgeBaseSourcesDialogComponent {
  protected readonly data = inject<KnowledgeBaseSourcesDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<KnowledgeBaseSourcesDialogComponent, KnowledgeBaseSourcesResult | undefined>>(MatDialogRef);
  private readonly snackbar = inject(SnackbarService);

  readonly readOnly = !!this.data.managedBy;
  private readonly initialIds = new Set(this.data.currentSources.map((s) => s.id));

  readonly search = signal('');
  readonly selectedIds = signal<Set<string>>(new Set(this.initialIds));
  /** True while `onApply` is in flight — disables the form so the selection can't be lost mid-request. */
  readonly submitting = signal(false);

  readonly selectedCount = computed(() => this.selectedIds().size);

  readonly filteredSources = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.data.availableSources;
    return this.data.availableSources.filter(
      (ks) => ks.name.toLowerCase().includes(q) || (ks.subtitle?.toLowerCase().includes(q) ?? false),
    );
  });

  readonly dirty = computed(() => {
    const current = this.selectedIds();
    if (current.size !== this.initialIds.size) return true;
    for (const id of current) {
      if (!this.initialIds.has(id)) return true;
    }
    return false;
  });

  isSelected(ks: SourcesDialogKnowledgeSource): boolean {
    return this.selectedIds().has(ks.id);
  }

  toggle(ks: SourcesDialogKnowledgeSource, checked: boolean): void {
    this.selectedIds.update((set) => {
      const next = new Set(set);
      if (checked) next.add(ks.id);
      else next.delete(ks.id);
      return next;
    });
  }

  cancel(): void {
    if (this.submitting()) return;
    this.dialogRef.close(undefined);
  }

  async apply(): Promise<void> {
    if (this.submitting()) return;
    const result: KnowledgeBaseSourcesResult = {
      kbId: this.data.kbId,
      sourceIds: Array.from(this.selectedIds()),
    };
    this.submitting.set(true);
    try {
      await this.data.onApply?.(result);
      this.dialogRef.close(result);
    } catch (err) {
      console.error('[knowledge-management] updateKnowledgeBaseSources failed', err);
      this.snackbar.openErrorSnack('KNOWLEDGE_SHARING.SOURCES.SAVE_ERROR');
    } finally {
      this.submitting.set(false);
    }
  }

  iconFor(kind: SourcesDialogKSKind): string {
    switch (kind) {
      case 'url': return 'language';
      case 'pdf': return 'picture_as_pdf';
      case 'file': return 'description';
      case 'custom': return 'notes';
      default: return 'description';
    }
  }

  trackKSId = (_: number, ks: SourcesDialogKnowledgeSource): string => ks.id;
}
